import {
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSForm,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useCallback } from "react";
import { IWorkLog } from "@wingspanhq/payments/dist/interfaces";
import { useMutationRejectWorkLog } from "../../../../query/workLog/mutations/useMutationRejectWorkLog";
import { PublicCommentField } from "../../components/PublicCommentField";

type FormData = {
  comment: string;
};

type Props = {
  workLog: IWorkLog;
  contractorName?: string;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalRejectWorkLog: React.FC<Props> = ({
  workLog,
  contractorName = "the contractor",
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [rejectWorkLog, rejectMeta] = useMutationRejectWorkLog();

  const handleReject = useCallback(
    async (formData: FormData) => {
      rejectWorkLog(
        {
          workLogId: workLog.workLogId,
          comment: formData.comment
        },
        {
          onSuccess: () => {
            modalProps.onClose({ success: true });
            openSnackbar({
              icon: { name: "check" },
              message: "Work log rejected"
            });
          },
          onError: () => {
            openSnackbar({
              type: "warning",
              message: "Failed to reject work log. Please try again."
            });
          }
        }
      );
    },
    [openSnackbar, modalProps, rejectWorkLog, workLog.workLogId]
  );

  return (
    <WSModal title="Reject work log" {...modalProps}>
      <WSForm<FormData> onSubmit={handleReject} defaultValues={{ comment: "" }}>
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            Are you sure you want to reject the work log?
          </WSText.ParagraphSm>

          <WSAlert
            theme="info"
            title="About rejected work logs"
            icon="info-circle"
          >
            Once a work log is rejected, it cannot be reverted. It will not be
            invoiced. Please provide the necessary detail to your contractor
            about the rejection.
          </WSAlert>

          <PublicCommentField
            description={`Comment is visible to both you and the contractor. Please provide specific details that will inform ${contractorName} about the decision.`}
            placeholder="Provide specific details about the decision"
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "No, go back",
                type: "button",
                onClick: () => {
                  modalProps.onClose({ success: false });
                }
              },
              {
                kind: "Primary",
                destructive: true,
                label: "Yes, reject work log",
                loading: rejectMeta.isLoading
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};

export const useModalRejectWorkLog = () => useWSModalCustom(ModalRejectWorkLog);
